// NEWS VIDEO — the pictures, not just the words.
//
// Two kinds of video reach the desk:
//
//  1. Clips carried inside the story's own feed item. Plenty of wires attach a
//     video or a still to each item (<media:content>, <enclosure>), and those
//     play straight from the publisher with no third party involved.
//  2. Video wires: the upload feeds of the broadcast newsrooms. These are plain
//     public XML — no key, no account — and each entry gives a video id we can
//     embed. That is how the desk gets moving pictures for the whole cycle
//     rather than only for whichever outlet bothered to attach a file.
//
// Everything is pulled through her own relay, so no publisher and no video host
// ever sees your address until you press play.
//
// Watermark: Sophia · Skye-Net

export interface VideoWire {
  id: string;
  label: string;
  note: string;
  /** YouTube channel id whose public upload feed is read. */
  channelId: string;
}

/** Broadcast newsrooms whose upload feeds are public XML. Verified live. */
export const VIDEO_WIRES: VideoWire[] = [
  { id: "reuters", label: "Reuters", note: "wire video", channelId: "UChqUTb7kYRX8-EiaN3XFrSQ" },
  { id: "ap", label: "Associated Press", note: "wire video", channelId: "UC52X5wxOL_s5yw0dQk7NtgA" },
  { id: "bbc", label: "BBC News", note: "UK & world", channelId: "UC16niRr50-MSBwiO3YDb3RA" },
  { id: "sky", label: "Sky News", note: "UK", channelId: "UCoMdktPbSTixAyNGwb-UYkQ" },
  { id: "guardian", label: "Guardian News", note: "UK", channelId: "UCIRYBXDze5krPDzAEOxFGVA" },
  { id: "aljazeera", label: "Al Jazeera English", note: "world", channelId: "UCNye-wNBqNL5ZzHSJj3l8Bg" },
  { id: "dw", label: "DW News", note: "Europe", channelId: "UCknLrEdhRCp1aegoMqRaCZg" },
  { id: "france24", label: "France 24 English", note: "Europe & Africa", channelId: "UCQfwfsi5VrQ8yKZ-UWmAEFg" },
  { id: "pbs", label: "PBS NewsHour", note: "US", channelId: "UC6ZFN9Tx6xh-skXCuRHCDpQ" },
  { id: "nasa", label: "NASA", note: "science & launches", channelId: "UCLA_DiR1FfKNvjuUpBHmylQ" },
];

export interface NewsClip {
  /** YouTube video id. */
  videoId: string;
  title: string;
  wire: string;
  /** Publication time, ms since epoch — 0 when the feed omitted one. */
  at: number;
  thumb: string;
  description: string;
}

function relay(url: string): string {
  return `/api/public/frame?${new URLSearchParams({ url, dnt: "1" }).toString()}`;
}

function decode(s: string): string {
  return s
    .replace(/&lt;/g, "<").replace(/&gt;/g, ">").replace(/&quot;/g, '"')
    .replace(/&#39;|&apos;/g, "'").replace(/&nbsp;/g, " ").replace(/&amp;/g, "&")
    .replace(/&#(\d+);/g, (_, n) => String.fromCharCode(Number(n)));
}

function pick(raw: string, tag: string): string {
  const m = new RegExp(`<${tag}[^>]*>([\\s\\S]*?)<\\/${tag}>`, "i").exec(raw);
  return decode((m?.[1] ?? "").replace(/<!\[CDATA\[|\]\]>/g, "").replace(/<[^>]+>/g, " ").trim());
}

/** One newsroom's latest video bulletins. */
export async function loadVideoWire(wire: VideoWire, cap = 15): Promise<NewsClip[]> {
  const url = `https://www.youtube.com/feeds/videos.xml?channel_id=${encodeURIComponent(wire.channelId)}`;
  const res = await fetch(relay(url));
  if (!res.ok) throw new Error(`${wire.label} video wire did not answer (${res.status})`);
  const xml = await res.text();
  const entries = xml.match(/<entry[\s\S]*?<\/entry>/gi) ?? [];
  return entries.slice(0, cap).map((raw) => {
    const videoId = /<yt:videoId>([^<]+)<\/yt:videoId>/i.exec(raw)?.[1] ?? "";
    const when = pick(raw, "published") || pick(raw, "updated");
    const at = when ? Date.parse(when) : 0;
    return {
      videoId,
      title: pick(raw, "title") || "(untitled bulletin)",
      wire: wire.label,
      at: Number.isFinite(at) ? at : 0,
      thumb: /<media:thumbnail[^>]+url=["']([^"']+)["']/i.exec(raw)?.[1]
        ?? (videoId ? `https://i.ytimg.com/vi/${videoId}/hqdefault.jpg` : ""),
      description: pick(raw, "media:description").slice(0, 400),
    };
  }).filter((c) => c.videoId);
}

/**
 * Every video wire at once, newest first. A newsroom that does not answer is
 * named rather than allowed to hold up the rest.
 */
export async function loadVideoDesk(
  wires: readonly VideoWire[] = VIDEO_WIRES,
): Promise<{ clips: NewsClip[]; fetchedAt: number; silent: { wire: string; why: string }[] }> {
  const results = await Promise.all(
    wires.map(async (w) => {
      try { return { wire: w, clips: await loadVideoWire(w), why: "" }; }
      catch (e) { return { wire: w, clips: [] as NewsClip[], why: e instanceof Error ? e.message : "no answer" }; }
    }),
  );
  const clips: NewsClip[] = [];
  const silent: { wire: string; why: string }[] = [];
  const seen = new Set<string>();
  for (const r of results) {
    if (r.why || !r.clips.length) { silent.push({ wire: r.wire.label, why: r.why || "answered with nothing" }); continue; }
    for (const c of r.clips) {
      if (seen.has(c.videoId)) continue;
      seen.add(c.videoId);
      clips.push(c);
    }
  }
  clips.sort((a, b) => b.at - a.at);
  return { clips, fetchedAt: Date.now(), silent };
}

/** Privacy-preserving embed for one clip. Muted start so audio can be unlocked deliberately. */
export function clipEmbed(videoId: string): string {
  const origin = typeof window === "undefined" ? "" : window.location.origin;
  const q = new URLSearchParams({
    autoplay: "1", mute: "1", playsinline: "1", enablejsapi: "1", rel: "0", modestbranding: "1",
    ...(origin ? { origin } : {}),
  });
  return `https://www.youtube-nocookie.com/embed/${encodeURIComponent(videoId)}?${q.toString()}`;
}

/**
 * Match video bulletins to a written headline. Purely local word overlap — no
 * search service, no query leaving the device. Anything scoring under a third
 * of the headline's substantial words is not offered, because a wrong clip
 * beside a story is worse than no clip.
 */
export function clipsForHeadline(headline: string, clips: readonly NewsClip[], cap = 3): NewsClip[] {
  const words = new Set(
    headline.toLowerCase().replace(/[^a-z0-9 ]/g, " ").split(/\s+/).filter((w) => w.length > 3),
  );
  if (words.size < 2) return [];
  const scored = clips.map((c) => {
    const theirs = c.title.toLowerCase().replace(/[^a-z0-9 ]/g, " ").split(/\s+/).filter((w) => w.length > 3);
    let hit = 0;
    for (const w of new Set(theirs)) if (words.has(w)) hit += 1;
    return { clip: c, score: hit / words.size };
  });
  return scored
    .filter((s) => s.score >= 0.34)
    .sort((a, b) => b.score - a.score || b.clip.at - a.clip.at)
    .slice(0, cap)
    .map((s) => s.clip);
}
